import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@journ/ui/table'
import { flexRender } from '@/libs/table'
import type { Table as ReactTable } from '@/libs/table'
import { HorizontalPagination } from '@/components/horizontal-pagination'
import { tableHead, tableCell } from '@/components/table-styles'
import type { ColumnMeta } from '@/components/table-styles'
import { useTableScroll } from '@/hooks/use-table-scroll'

type FullColumnMeta = ColumnMeta & { headerClassName?: string }

type Props<TData> = {
	table: ReactTable<TData>
	sortBy: string | null | undefined
	sortDir: string | null | undefined
	sortableColumns: Set<string>
	onSort: (key: string) => void
	onRowClick?: (id: string) => void
	height?: string
	rowHeight?: number
}

const DataTable = <TData,>({
	table,
	sortBy,
	sortDir,
	sortableColumns,
	onSort,
	onRowClick,
	height = 'auto',
	rowHeight = 45,
}: Props<TData>) => {
	const scroll = useTableScroll()

	return (
		<div ref={scroll.containerRef} className='overflow-auto overscroll-none scrollbar-hide' style={{ height }}>
			<Table className='w-full'>
				<TableHeader className='sticky top-0 z-30 bg-background block'>
					{table.getHeaderGroups().map(headerGroup => {
						const headers = headerGroup.headers
						const lastHeaderId = headers[headers.length - 1]?.column.id ?? ''
						return (
							<TableRow key={headerGroup.id} className=' hover:bg-transparent flex items-center border-b-0!'>
								{headers.map((header, headerIndex) => {
									const meta = header.column.columnDef.meta as FullColumnMeta | undefined
									const isName = header.column.id === 'name'
									const isSortable = sortableColumns.has(header.column.id)
									const isActive = sortBy === header.column.id
									const isLastBeforeActions = headerIndex === headers.length - 2 && lastHeaderId === 'actions'
									const headerStyle = isLastBeforeActions
										? { width: header.getSize(), flex: 1 }
										: { width: header.getSize() }
									return (
										<TableHead
											key={header.id}
											className={tableHead({ sticky: meta?.sticky, class: meta?.headerClassName ?? meta?.className })}
											style={headerStyle}
										>
											{isName ? (
												<div className='flex items-center justify-between w-full overflow-hidden'>
													<div className='min-w-0 overflow-hidden'>
														{isSortable ? (
															<Button
																variant='ghost'
																className='p-0 hover:bg-transparent gap-1 h-auto'
																onClick={() => onSort(header.column.id)}
															>
																<span className='truncate'>
																	{flexRender(header.column.columnDef.header, header.getContext())}
																</span>
																{isActive && sortDir === 'asc' && <Icons.ArrowDownward size={14} />}
																{isActive && sortDir === 'desc' && <Icons.ArrowUpward size={14} />}
															</Button>
														) : (
															<span className='truncate'>
																{flexRender(header.column.columnDef.header, header.getContext())}
															</span>
														)}
													</div>
													{scroll.isScrollable && (
														<HorizontalPagination
															canScrollLeft={scroll.canScrollLeft}
															canScrollRight={scroll.canScrollRight}
															onScrollLeft={scroll.scrollLeft}
															onScrollRight={scroll.scrollRight}
															className='hidden md:flex shrink-0'
														/>
													)}
												</div>
											) : (
												<>
													{isSortable ? (
														<Button
															variant='ghost'
															className='p-0 hover:bg-transparent gap-1 h-auto'
															onClick={() => onSort(header.column.id)}
														>
															<span className='truncate'>
																{flexRender(header.column.columnDef.header, header.getContext())}
															</span>
															{isActive && sortDir === 'asc' && <Icons.ArrowDownward size={14} />}
															{isActive && sortDir === 'desc' && <Icons.ArrowUpward size={14} />}
														</Button>
													) : (
														<span className='truncate text-muted-foreground'>
															{flexRender(header.column.columnDef.header, header.getContext())}
														</span>
													)}
												</>
											)}
											{header.column.getCanResize() && (
												<div
													onMouseDown={header.getResizeHandler()}
													onTouchStart={header.getResizeHandler()}
													className='absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none opacity-0 hover:opacity-100 bg-border'
												/>
											)}
										</TableHead>
									)
								})}
							</TableRow>
						)
					})}
				</TableHeader>
				<TableBody
					style={{
						height: `${table.getRowModel().rows.length * rowHeight - 2}px`,
						position: 'relative',
					}}
				>
					{table.getRowModel().rows.map(row => {
						const cells = row.getVisibleCells()
						const lastCellId = cells[cells.length - 1]?.column.id ?? ''
						return (
							<TableRow
								key={row.id}
								className='group top-0 left-0 w-full min-w-full flex items-center border-0 hover:bg-hover-group'
								style={{ height: rowHeight }}
							>
								{cells.map((cell, cellIndex) => {
									const meta = cell.column.columnDef.meta as ColumnMeta | undefined
									const isActions = cell.column.id === 'actions'
									const isName = cell.column.id === 'name'
									const isLastBeforeActions = cellIndex === cells.length - 2 && lastCellId === 'actions'
									const cellStyle = isLastBeforeActions
										? { width: cell.column.getSize(), flex: 1 }
										: { width: cell.column.getSize() }
									return (
										<TableCell
											key={cell.id}
											className={tableCell({
												sticky: meta?.sticky,
												class: meta?.className,
												variant: isName ? 'name' : undefined,
											})}
											style={cellStyle}
											onClick={isActions || !onRowClick ? undefined : () => onRowClick(row.id)}
										>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									)
								})}
							</TableRow>
						)
					})}
				</TableBody>
			</Table>
		</div>
	)
}

export { DataTable }
